<script setup lang="ts">
import { computed, ref, toValue, type VNode, h } from 'vue';
import { useEventListener } from '@vueuse/core';
import {
    type NodeRepr,
    NodeKind,
    reprWithNodeKind,
    isNaryRepr,
    isTermRepr,
    isUnaryRepr,
    isBinRepr,
    reprWithChildren,
    reprWithLhsChild,
    reprGetLhsChild,
    reprGetRhsChild,
    reprGetChildren,
    reprWithRhsChild,
    isValueRepr,
    reprGetTerm,
    reprWithTerm,
    exprFromRepr,
    createTermRepr,
} from '../helpers/internal-repr';
import NodeKindSelector from './NodeKindSelector.vue';
import NodeReprListInput from './NodeReprListInput.vue';
import NodeReprDropZone from './NodeReprDropZone.vue';
import PredicateValueInput, {
    type PredicateValueInputMethod,
    type PredicateValueDisplayFormat,
} from './PredicateValueInput.vue';
import { reprGetValue } from '../helpers/internal-repr';
import { PredicateValue } from '@precon-tools-suite-by-shared/precon-graphql-types';
import { reprWithValue } from '../helpers/internal-repr';
import { useDraggableNodeRepr } from '../composables/drag-controller';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputText from 'primevue/inputtext';
import ContextMenu from 'primevue/contextmenu';
import type { MenuItem } from 'primevue/menuitem';
import B4Expr from '@precon-tools-suite-by-shared/gino-multi-valued-logic-purescript';

export type { PredicateValueInputMethod, PredicateValueDisplayFormat };

export type TermSlotProps = {
    modelValue: string | null;
    readonly: boolean;
    testValue: PredicateValue;
    'onUpdate:modelValue': (newValue: string | null) => void;
};

export type ValueSlotProps = {
    modelValue: PredicateValue;
    inputMethod: PredicateValueInputMethod;
    labelFormat: PredicateValueDisplayFormat;
    inputId: string;
    inputName: string;
    readonly: boolean;
    'onUpdate:modelValue': (newValue: PredicateValue) => void;
    options: PredicateValue[];
    symbolLabels: boolean;
};

const props = withDefaults(
    defineProps<{
        modelValue: NodeRepr;
        inputId: string;
        inputName: string;
        valueLabelFormat: PredicateValueDisplayFormat;
        valueInputMethod: PredicateValueInputMethod;
        renderValue: ((value: ValueSlotProps) => VNode[]) | null | undefined;
        renderTerm: ((props: TermSlotProps) => VNode[]) | null | undefined;
        parentKind?: NodeKind | null;
        path?: number[];
        readonly?: boolean;
        draggable?: boolean;
        allowEquiv?: boolean;
        allowNonRootValue?: boolean;
        allowUndecidableValue?: boolean;
        symbolLabels?: boolean;
        symbolValueLabels?: boolean;
        testTermBindings?: Record<string, PredicateValue> | null;
        testTautologies?: boolean;
        testContradictions?: boolean;
    }>(),
    {
        path: () => [],
        parentKind: null,
        testTermBindings: null,
        readonly: false,
    }
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: NodeRepr): void;
    (
        e: 'update:testTermBindings',
        value: Record<string, PredicateValue> | null
    ): void;
}>();

const model = computed({
    get: () => props.modelValue,
    set: (newValue: NodeRepr) => {
        emit('update:modelValue', newValue);
    },
});

const children = computed({
    get: () => reprGetChildren(model.value),
    set: (newValue: NodeRepr[]) => {
        model.value =
            reprWithChildren(newValue, model.value) ?? createTermRepr();
    },
});

const lhs = computed({
    get: () => reprGetLhsChild(model.value),
    set: (newValue: NodeRepr) => {
        model.value = reprWithLhsChild(newValue, model.value);
    },
});

const rhs = computed({
    get: () => reprGetRhsChild(model.value),
    set: (newValue: NodeRepr) => {
        model.value = reprWithRhsChild(newValue, model.value);
    },
});

const kind = computed({
    get: () => model.value.kind,
    set: (newValue: NodeKind) => {
        model.value = reprWithNodeKind(newValue, model.value);
    },
});

const kindOptions = computed(() => {
    const res: NodeKind[] = [];

    if (props.path.length === 0 || props.allowNonRootValue)
        res.push(NodeKind.Value);

    res.push(NodeKind.Term);
    if (props.parentKind !== NodeKind.Neg) res.push(NodeKind.Neg);
    if (props.parentKind !== NodeKind.Conj) res.push(NodeKind.Conj);
    if (props.parentKind !== NodeKind.Disj) res.push(NodeKind.Disj);

    res.push(NodeKind.Implies);

    if (props.allowEquiv) res.push(NodeKind.Equiv);

    return res;
});

const valueOptions = computed(() =>
    props.allowUndecidableValue
        ? [PredicateValue.Yes, PredicateValue.No, PredicateValue.Undecidable]
        : [PredicateValue.Yes, PredicateValue.No]
);

function updateTerm(newValue: string | null | undefined) {
    console.log(newValue);
    model.value = reprWithTerm(newValue ?? null, model.value);
}

const term = computed({
    get: () => reprGetTerm(model.value),
    set: updateTerm,
});

function updateValue(newValue: PredicateValue | null | undefined) {
    model.value = reprWithValue(
        newValue ?? PredicateValue.Indeterminate,
        model.value
    );
}

const value = computed({
    get: () => reprGetValue(model.value),
    set: updateValue,
});

const draggingPlaceholder = ref<HTMLDivElement>();

type Position = { x: number; y: number };

const { startDragging, dragging, childDragging } = useDraggableNodeRepr(
    model,
    () => props.path
);

const lastHeight = ref();
const lastWidth = ref();
const el = ref<HTMLDivElement>();
const elPosition = ref<Position>({ x: 0, y: 0 });
const elDragDelta = ref<Position>({ x: 0, y: 0 });
const elStyle = computed(() => {
    if (dragging.value) {
        return {
            height: `${lastHeight.value}px`,
            width: `${lastWidth.value}px`,
            left: `${elPosition.value.x}px`,
            top: `${elPosition.value.y}px`,
        };
    }

    return {};
});

function dragStartHandler(event: MouseEvent) {
    // Do nothing if readonly.
    if (props.readonly) return;

    // Check if primary mouse button.
    if (event.button) return;

    if (!dragging.value) {
        const containerRect = toValue(el)?.getBoundingClientRect();
        if (!containerRect) return;

        lastHeight.value = containerRect.height;
        lastWidth.value = containerRect.width;
        elPosition.value = {
            x: containerRect.left,
            y: containerRect.top,
        };
        elDragDelta.value = {
            x: event.clientX - containerRect.left,
            y: event.clientY - containerRect.top,
        };
        startDragging(event);
    }
}

useEventListener(document, 'pointermove', (event: MouseEvent) => {
    if (dragging.value) {
        const { x: deltaX, y: deltaY } = elDragDelta.value;
        const { clientX, clientY } = event;
        elPosition.value = { x: clientX - deltaX, y: clientY - deltaY };
    }
});

// We only use this computed to avoid re-building the expression
// on every termBinding change.
const expr = computed(() => {
    return (
        exprFromRepr(model.value)?.elimClassicalValues() ?? B4Expr.INDETERMINATE
    );
});

const testValue = computed(() => {
    const val = model.value;

    if (isValueRepr(val)) {
        return val.value;
    }

    const bindings = props.testTermBindings ?? {};
    if (isTermRepr(val)) {
        if (!val.term) return PredicateValue.Indeterminate;
        return bindings[val.term] ?? PredicateValue.Indeterminate;
    }

    const boundExpr = expr.value.bind(bindings);

    if (props.testTautologies && boundExpr.isTautology()) {
        return PredicateValue.Yes;
    }

    if (props.testContradictions && boundExpr.isContradiction()) {
        return PredicateValue.No;
    }

    return boundExpr.evaluated;
});

const TermSlot = function (p: TermSlotProps): VNode[] {
    if (props.renderTerm) return props.renderTerm(p);

    if (p.readonly)
        return [
            h(
                'div',
                {
                    class: `expr-term expr-term-readonly expr-term-test-${p.testValue.toLowerCase()} w-full`,
                    'data-predicate-value': p.testValue,
                },
                p.modelValue ?? undefined
            ),
        ];

    return [
        h(InputText, {
            ...p,
            class: `expr-term expr-term-test-${p.testValue.toLowerCase()} w-full`,
            'data-predicate-value': p.testValue,
        }),
    ];
};

const ValueSlot = function (p: ValueSlotProps): VNode[] {
    if (props.renderValue) return props.renderValue(p);

    return [
        h(PredicateValueInput, {
            ...p,
        }),
    ];
};

const menu = ref<ContextMenu>();

const contextMenuEnabled = computed(
    () =>
        props.testTermBindings !== null && isTermRepr(model.value) && menu.value
);

function setTestTermBindingValue(val: PredicateValue) {
    if (isTermRepr(model.value)) {
        const term = model.value.term;
        if (!term || !props.testTermBindings) return;

        if (props.testTermBindings[term] === val) {
            val = PredicateValue.Indeterminate;
        }

        emit('update:testTermBindings', {
            ...props.testTermBindings,
            [term]: val,
        });
    }
}

const contextMenuItems = computed<MenuItem[]>(() => [
    {
        label: 'Testwaarde',
        class: 'font-bold',
        disabled: true,
    },
    {
        label: 'Ja',
        class:
            'hover:bg-green-200' +
            (testValue.value === PredicateValue.Yes ? ' bg-green-200' : ''),
        command: () => {
            setTestTermBindingValue(PredicateValue.Yes);
        },
    },
    {
        label: 'Nee',
        class:
            'hover:bg-pink-200' +
            (testValue.value === PredicateValue.No ? ' bg-pink-200' : ''),
        command: () => {
            setTestTermBindingValue(PredicateValue.No);
        },
    },
    {
        label: 'Onbeslisbaar',
        class:
            'hover:bg-yellow-200' +
            (testValue.value === PredicateValue.Undecidable
                ? ' bg-yellow-200'
                : ''),
        command: () => {
            setTestTermBindingValue(PredicateValue.Undecidable);
        },
    },
]);

function contextMenuHandler(event: MouseEvent) {
    if (contextMenuEnabled.value && menu.value) {
        menu.value.show(event);
        event.preventDefault();
    }
}
</script>

<template>
    <ContextMenu ref="menu" :model="contextMenuItems" />

    <div
        v-if="dragging"
        ref="draggingPlaceholder"
        class="expr-node-repr-input-drag-placeholder"
        :style="{ height: `${lastHeight}px` }"
    ></div>

    <div
        v-bind="$attrs"
        ref="el"
        class="expr-node-repr-input"
        :class="{ dragging }"
        :style="elStyle"
        :data-node-kind="kind"
        @contextmenu="contextMenuHandler"
    >
        <NodeReprDropZone
            class="node-info-input"
            :parent-repr="model"
            :severity="childDragging ? 'danger' : 'warning'"
            :path="path"
        >
            <InputGroup style="height: 100%">
                <InputGroupAddon
                    v-if="draggable && !readonly"
                    class="drag-handle"
                    @pointerdown="dragStartHandler"
                >
                    <i class="pi pi-bars"></i>
                </InputGroupAddon>
                <NodeKindSelector
                    v-if="!readonly || kind !== 'term'"
                    v-model="kind"
                    :readonly="readonly"
                    :options="kindOptions"
                    :input-id="`${inputId}-kind`"
                    :input-name="`${inputName}-kind`"
                    :symbol-labels="symbolLabels"
                    :test-value="testValue"
                    :data-test-value="testValue"
                />
            </InputGroup>
        </NodeReprDropZone>
        <div class="node-value-input" style="width: 100%">
            <template v-if="isNaryRepr(model)">
                <NodeReprListInput
                    v-model="children"
                    :readonly="readonly"
                    :render-term="renderTerm"
                    :render-value="renderValue"
                    :parent-kind="kind"
                    :parent-path="path"
                    :parent-input-id="inputId"
                    :parent-input-name="inputName"
                    :value-label-format="valueLabelFormat"
                    :value-input-method="valueInputMethod"
                    :allow-equiv="allowEquiv"
                    :allow-non-root-value="allowNonRootValue"
                    :allow-undecidable-value="allowUndecidableValue"
                    :symbol-labels="symbolLabels"
                    :symbol-value-labels="symbolValueLabels"
                    :test-term-bindings="testTermBindings"
                    :test-tautologies="testTautologies"
                    :test-contradictions="testContradictions"
                    @update:test-term-bindings="
                        emit('update:testTermBindings', $event)
                    "
                />
            </template>
            <template v-else-if="isValueRepr(model)">
                <ValueSlot
                    v-bind="{
                        modelValue: value,
                        inputMethod: valueInputMethod,
                        labelFormat: valueLabelFormat,
                        inputId: `${inputId}-value`,
                        inputName: `${inputName}-value`,
                        'onUpdate:modelValue': updateValue,
                        readonly: readonly,
                        options: valueOptions,
                        symbolLabels: symbolValueLabels ?? false,
                    }"
                />
            </template>
            <template v-else-if="isTermRepr(model)">
                <TermSlot
                    v-bind="{
                        modelValue: term,
                        readonly: readonly,
                        testValue: testValue,
                        'onUpdate:modelValue': updateTerm,
                    }"
                />
            </template>
            <template v-else-if="isUnaryRepr(model)">
                <NodeReprInput
                    v-model="lhs"
                    draggable
                    :readonly="readonly"
                    :render-term="renderTerm"
                    :render-value="renderValue"
                    :path="[...path, 0]"
                    :parent-kind="kind"
                    :value-label-format="valueLabelFormat"
                    :value-input-method="valueInputMethod"
                    :input-id="`${inputId}-0`"
                    :input-name="`${inputName}-0`"
                    :allow-equiv="allowEquiv"
                    :allow-non-root-value="allowNonRootValue"
                    :allow-undecidable-value="allowUndecidableValue"
                    :symbol-labels="symbolLabels"
                    :symbol-value-labels="symbolValueLabels"
                    :test-term-bindings="testTermBindings"
                    :test-tautologies="testTautologies"
                    :test-contradictions="testContradictions"
                    @update:test-term-bindings="
                        emit('update:testTermBindings', $event)
                    "
                />
            </template>
            <template v-else-if="isBinRepr(model)">
                <div class="expr-node-repr-bin-children">
                    <NodeReprInput
                        v-model="lhs"
                        draggable
                        :readonly="readonly"
                        :render-term="renderTerm"
                        :render-value="renderValue"
                        :path="[...path, 0]"
                        :parent-kind="kind"
                        :value-label-format="valueLabelFormat"
                        :value-input-method="valueInputMethod"
                        :input-id="`${inputId}-0`"
                        :input-name="`${inputName}-0`"
                        :allow-equiv="allowEquiv"
                        :allow-non-root-value="allowNonRootValue"
                        :allow-undecidable-value="allowUndecidableValue"
                        :symbol-labels="symbolLabels"
                        :symbol-value-lables="symbolValueLabels"
                        :test-term-bindings="testTermBindings"
                        :test-tautologies="testTautologies"
                        :test-contradictions="testContradictions"
                        @update:test-term-bindings="
                            emit('update:testTermBindings', $event)
                        "
                    />
                    <NodeReprInput
                        v-model="rhs"
                        draggable
                        :readonly="readonly"
                        :render-term="renderTerm"
                        :render-value="renderValue"
                        :path="[...path, 1]"
                        :parent-kind="kind"
                        :value-label-format="valueLabelFormat"
                        :value-input-method="valueInputMethod"
                        :input-id="`${inputId}-1`"
                        :input-name="`${inputName}-1`"
                        :allow-equiv="allowEquiv"
                        :allow-non-root-value="allowNonRootValue"
                        :allow-undecidable-value="allowUndecidableValue"
                        :symbol-labels="symbolLabels"
                        :symbol-value-labels="symbolValueLabels"
                        :test-term-bindings="testTermBindings"
                        :test-tautologies="testTautologies"
                        :test-contradictions="testContradictions"
                        @update:test-term-bindings="
                            emit('update:testTermBindings', $event)
                        "
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<style lang="scss">
.expr-node-repr-input-drag-placeholder {
    border: 2px dotted var(--primary-color);
    border-radius: var(--border-radius);
    width: 100%;
}

.expr-term.expr-term-readonly {
    padding: 0.75rem;
    border: 1px solid var(--surface-border);
    border-radius: var(--border-radius);
}

.expr-term.expr-term-test-yes {
    background-color: var(--green-100);
    border-color: var(--green-200);
}

.expr-term.expr-term-test-no {
    background-color: var(--pink-100);
    border-color: var(--pink-200);
}

.expr-term.expr-term-test-undecidable {
    background-color: var(--yellow-100);
    border-color: var(--surface-border);
}

.expr-node-repr-input {
    display: flex;
    width: 100%;

    &.dragging {
        position: fixed;
        opacity: 0.5;
        z-index: 1000;
    }

    .drag-handle {
        cursor: grab;
    }
}
.then-item {
    display: flex;
    align-items: center;
    .then {
        padding: 0.5rem;
        font-weight: bold;
        color: var(--secondary-color);
    }
}
</style>
